// stores/audioStore.ts
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useLogStore } from './LogStore'

export const useIDBStore = defineStore('IDB', () => {

  // --------------------------------------------------------
  // CONSTANTS 
  // --------------------------------------------------------

  const LOG_GROUP = 'IDB'

  const IDB_NAME = 'edito'
  const IDB_VERSION = 2
  const AUDIO_FILE_STORE_NAME = 'audio'
  const CHART_FILE_STORE_NAME = 'chart'

  const logStore = useLogStore()

  // --------------------------------------------------------
  // STATE 
  // --------------------------------------------------------

  let db = null
  const audioDataTitles = ref([])
  const currentAudioTitle = ref(null)
  const currentAudioChartTitles = ref([])

  const dbOpenRequest = window.indexedDB.open(IDB_NAME, IDB_VERSION)

  dbOpenRequest.onerror = (event) => {
    logStore.sendLog(LOG_GROUP, `Error loading database: ${event.target.error}`, event)
  }

  dbOpenRequest.onsuccess = (event) => {
    logStore.sendLog(LOG_GROUP, "Database initialized.")
    db = event.target.result
    retrieveAudioDataTitles()
    retrieveChartDataTitles()
  }

  dbOpenRequest.onupgradeneeded = (event) => {
    db = event.target.result;
    if (!db.objectStoreNames.contains(AUDIO_FILE_STORE_NAME)) {
      db.createObjectStore(AUDIO_FILE_STORE_NAME, { keyPath: 'title' });
      logStore.sendLog(LOG_GROUP, "Audio object store created.")
    }

    if (!db.objectStoreNames.contains(CHART_FILE_STORE_NAME)) {
      db.createObjectStore(CHART_FILE_STORE_NAME, { keyPath: ['audioTitle', 'chartTitle'] });
      logStore.sendLog(LOG_GROUP, "Chart object store created.")
    }
  }

  // --------------------------------------------------------
  // GETTERS 
  // --------------------------------------------------------

  const retrieveAudioDataTitles = () => {
    const transaction = db.transaction(AUDIO_FILE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(AUDIO_FILE_STORE_NAME);

    const req = store.getAllKeys()
    req.onsuccess = (e) => {
      logStore.sendLog(LOG_GROUP, `Audio file titles read successful. Read ${e.target.result.length} items`)
      audioDataTitles.value = e.target.result
    }
  }

  const getAudioDataValue = (key) => {
    const transaction = db.transaction(AUDIO_FILE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(AUDIO_FILE_STORE_NAME);

    const req = store.get(key)
    return new Promise((resolve, reject) => {
      req.onsuccess = (e) => {
        logStore.sendLog(LOG_GROUP, `Audio file data ${e.target.result.title} read successfuly.`)
        resolve(e.target.result.value)
      }
      req.onerror = (e) => {
        logStore.sendLog(LOG_GROUP, `Failed to read audio file data: ${e.target.error}`, e)
        reject(e)
      }
    })
  }

  const retrieveChartDataTitles = () => {
    const transaction = db.transaction(CHART_FILE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(CHART_FILE_STORE_NAME);

    const title = currentAudioTitle.value == "" || currentAudioTitle.value == null ? "orphan charts" : currentAudioTitle.value

    const range = IDBKeyRange.bound([title], [title, []]);

    const req = store.getAllKeys(range)
    req.onsuccess = (e) => {
      logStore.sendLog(LOG_GROUP, `Chart titles read successful. Read ${e.target.result.length} items`)
      currentAudioChartTitles.value = e.target.result.map(x => x[1])
    }
  }

  const getChartDataValue = key => {
    const transaction = db.transaction(CHART_FILE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(CHART_FILE_STORE_NAME);

    const title = currentAudioTitle.value == "" || currentAudioTitle.value == null ? "orphan charts" : currentAudioTitle.value

    const req = store.get([title, key])
    return new Promise((resolve, reject) => {
      req.onsuccess = (e) => {
        if(!e.target.result){
          logStore.sendLog(LOG_GROUP, `Failed to read chart data: [${title}, ${key}], not found in database`)
          return 
        }
        logStore.sendLog(LOG_GROUP, `Chart data "${e.target.result.chartTitle}" read successfuly.`)
        resolve(e.target.result.value)
      }
      req.onerror = (e) => {
        logStore.sendLog(LOG_GROUP, `Failed to read chart data: "${e.target.error}"`, e)
        reject(e)
      }
    })
  }

  // --------------------------------------------------------
  // ACTIONS 
  // --------------------------------------------------------

  const removeAudioDataItem = (key) => {
    // make charts orphan
    const transaction = db.transaction(CHART_FILE_STORE_NAME, 'readwrite', { 'durability': 'strict' });
    const store = transaction.objectStore(CHART_FILE_STORE_NAME);

    const range = IDBKeyRange.bound([key], [key, []]);
    const getReq = store.getAllRecords({ query: range })
    getReq.onsuccess = (e) => {
      e.target.result.forEach(x => {
        store.delete([key, x.key[1]])
        store.put({ audioTitle: 'orphan charts', chartTitle: x.key[1], value: x.value.value })
      })
    }


    // // delete audio
    const delTransaction = db.transaction(AUDIO_FILE_STORE_NAME, 'readwrite', { 'durability': 'strict' });
    const audioStore = delTransaction.objectStore(AUDIO_FILE_STORE_NAME);
    const delReq = audioStore.delete(key)
    delReq.onsuccess = e => {
      logStore.sendLog(LOG_GROUP, `Audio file "${key}" successfuly deleted from local database.`)
      const index = audioDataTitles.value.indexOf(key);
      if (index > -1) audioDataTitles.value.splice(index, 1);
    }
    delReq.onerror = e => logStore.sendLog(LOG_GROUP, `Failed to delete audio file to local database: ${e.target.error}`, e)

    if(currentAudioTitle.value == key) {
      retrieveChartDataTitles()
    }
  }

  const setAudioDataItem = (title, value) => {
    const transaction = db.transaction(AUDIO_FILE_STORE_NAME, 'readwrite', { 'durability': 'strict' });
    const store = transaction.objectStore(AUDIO_FILE_STORE_NAME);

    const req = store.put({ title, value })
    req.onsuccess = e => {
      logStore.sendLog(LOG_GROUP, `Audio file "${title}" successfuly written to local database.`)
      if (!audioDataTitles.value.includes(title))
        audioDataTitles.value.push(e.target.result)
    }
    req.onerror = e => logStore.sendLog(LOG_GROUP, `Failed to write audio file to local database: ${e.target.error}`, e)
  }

  const removeChartDataItem = key => {
    const transaction = db.transaction(CHART_FILE_STORE_NAME, 'readwrite', { 'durability': 'strict' });
    const store = transaction.objectStore(CHART_FILE_STORE_NAME);

    const title = currentAudioTitle.value == "" || currentAudioTitle.value == null ? "orphan charts" : currentAudioTitle.value

    const req = store.delete([title, key])
    req.onsuccess = e => {
      logStore.sendLog(LOG_GROUP, `Chart "${key}" successfuly deleted from local database.`)
      const index = currentAudioChartTitles.value.indexOf(key);
      if (index > -1) currentAudioChartTitles.value.splice(index, 1);
    }
    req.onerror = e => logStore.sendLog(LOG_GROUP, `Failed to delete chart to local database: ${e.target.error}`, e)
  }

  const setChartDataItem = (chartTitle, value) => {
    const transaction = db.transaction(CHART_FILE_STORE_NAME, 'readwrite', { 'durability': 'strict' });
    const store = transaction.objectStore(CHART_FILE_STORE_NAME);

    if (currentAudioTitle.value == "orphan charts") currentAudioTitle.value = "orphan charts.audio"

    if (currentAudioTitle.value == null || currentAudioTitle.value == "") {
      currentAudioTitle.value = "orphan charts"
    }

    const req = store.put({ audioTitle: currentAudioTitle.value, chartTitle, value })
    req.onsuccess = e => {
      logStore.sendLog(LOG_GROUP, `Chart "${chartTitle}" (audio "${currentAudioTitle.value}") successfuly written to local database.`)
      if (!currentAudioChartTitles.value.includes(chartTitle))
        currentAudioChartTitles.value.push(chartTitle)
    }
    req.onerror = e => logStore.sendLog(LOG_GROUP, `Failed to write chart to local database: ${e.target.error}`, e)
  }


  const setCurrentAudioTitle = value => {
    currentAudioTitle.value = value
    retrieveChartDataTitles()
  }

  // --------------------------------------------------------
  // MISC
  // --------------------------------------------------------

  return {
    setCurrentAudioTitle,
    setChartDataItem,
    getChartDataValue,
    removeChartDataItem,
    setAudioDataItem,
    getAudioDataValue,
    removeAudioDataItem,
    audioDataTitles,
    currentAudioTitle,
    currentAudioChartTitles,
  }
})
